import React from 'react';
import type { ReportView } from 'shared-models/browser';
import { formatMoney, isolateAmountLtr, roundMoney } from '../../../../utils/money';
import classes from './pos-report-view.module.scss';

const israelTimeZone = 'Asia/Jerusalem';

const valueFor = (iso: string, options: Intl.DateTimeFormatOptions): string =>
  new Intl.DateTimeFormat('he-IL-u-nu-latn', { timeZone: israelTimeZone, ...options }).format(new Date(iso));

const isValidDate = (iso: string | null): iso is string => iso !== null && !Number.isNaN(Date.parse(iso));

const formatShiftDate = (iso: string | null): string => {
  if (!isValidDate(iso)) return '--';
  const date = valueFor(iso, { day: '2-digit', month: '2-digit', year: '2-digit' });
  const weekday = valueFor(iso, { weekday: 'long' });
  return `${date} - ${weekday}`;
};

const formatPrintDateTime = (iso: string): string => {
  if (!isValidDate(iso)) return 'הופק בתאריך --';
  const date = valueFor(iso, { day: '2-digit', month: '2-digit', year: '2-digit' });
  const time = valueFor(iso, { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });
  return `הופק בתאריך ${date} בשעה ${time}`;
};

const Money: React.FC<{ value: number }> = ({ value }) => <span>{isolateAmountLtr(formatMoney(value))} ₪</span>;

const Row: React.FC<{ label: string; value: React.ReactNode; bold?: boolean }> = ({ label, value, bold = false }) => (
  <div className={bold ? `${classes.row} ${classes.rowBold}` : classes.row}>
    <span className={classes.rowLabel}>{label}</span>
    <span className={classes.rowValue}>{value}</span>
  </div>
);

export const WorkerActivityReport: React.FC<{ report: ReportView; variant?: 'screen' | 'print' }> = ({
  report,
  variant = 'screen',
}) => (
  <div
    className={classes.workerActivityReport}
    data-testid={variant === 'screen' ? 'worker-activity-report' : undefined}
  >
    <div className={classes.workerMeta}>
      <div className={classes.workerReportTitle}>דוח פעילות לעובד</div>
      <div>{formatShiftDate(report.closeTime ?? report.openTime)}</div>
      <div className={classes.metaRow}>{formatPrintDateTime(report.printTime)}</div>
    </div>

    <div className={classes.workerEmployeeTitle}>דוח פעילות להזמנות באחריות של {report.workerName ?? '--'}</div>

    <div className={classes.section}>
      <Row label='סה&quot;כ מכירות' value={<Money value={report.closedRevenue} />} />
      <Row label='הזמנות פתוחות' value={<Money value={report.openOrdersTotal} />} />
    </div>
    <div className={classes.workerDailyTotal}>
      <Row label='סה&quot;כ יומי' value={<Money value={report.salesWithOpenOrders} />} bold />
    </div>

    <div className={classes.section}>
      <div className={classes.workerSectionTitle}>תשלומים</div>
      <div className={classes.workerPaymentGrid}>
        <span />
        <span>הזמנות סגורות</span>
        <span>הזמנות פתוחות</span>
        <span>מזומן</span>
        <Money value={report.closedPaymentsCash} />
        <Money value={report.openPaymentsCash} />
        <span>אשראי</span>
        <Money value={report.closedPaymentsCredit} />
        <Money value={report.openPaymentsCredit} />
      </div>
    </div>

    <div className={classes.section}>
      <div className={classes.workerSectionTitle}>טיפים</div>
      <Row label='מזומן' value={<Money value={report.tipsCash} />} />
      <Row label='אשראי' value={<Money value={report.tipsCredit} />} />
    </div>

    <div className={classes.section}>
      <Row label="מס' סועדים" value={report.dinersCount} />
      <Row label='ממוצע לסועד' value={<Money value={report.avgPerDiner} />} />
      <Row label='החזרות' value={report.canceledOrderlinesCount} />
      <Row label='OTH מנות' value={report.othItemsCount} />
      <Row label='OTH חשבון' value={report.othOrdersCount} />
    </div>

    <div className={classes.section}>
      <Row
        label='מזומן להפקדה'
        value={<Money value={roundMoney(report.closedPaymentsCash + report.openPaymentsCash - report.tipsCredit)} />}
        bold
      />
    </div>
  </div>
);